import { useState, useEffect } from "react";
import { 
  Mic, 
  Square, 
  Copy, 
  Download, 
  CheckCircle, 
  Sparkles, 
  Languages, 
  Volume2,
  Menu
} from "lucide-react";

export default function HeroAppMockup() {
  const [phase, setPhase] = useState(0);
  const [displayText, setDisplayText] = useState("");
  const [activeAction, setActiveAction] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [textColor, setTextColor] = useState("#a0aec0");
  const [recordingTime, setRecordingTime] = useState(0);

  // Demo content - shorter to fit better
  const transcribed = "hey can you send me the report by five pm tomorrow thanks";
  const fixed = "Hey, can you send me the report by 5 PM tomorrow? Thanks.";
  const professional = "Could you please send me the report by 5 PM tomorrow? Thank you.";
  const translated = "¿Podrías enviarme el informe antes de las 5 PM mañana? Gracias.";

  useEffect(() => {
    let timeout;
    let charIndex = 0;
    let isMounted = true;
    let timerInterval;

    const runDemo = () => {
      if (!isMounted) return;
      
      // Reset state
      setPhase(0);
      setDisplayText("");
      setIsRecording(false);
      setTextColor("#a0aec0");
      setActiveAction(null);
      setRecordingTime(0);
      charIndex = 0;

      // Phase 1: Start Recording
      timeout = setTimeout(() => {
        if (!isMounted) return;
        setPhase(1);
        setIsRecording(true);
        
        // Start timer
        timerInterval = setInterval(() => {
          setRecordingTime(prev => prev + 1);
        }, 1000);

        // Type out transcription
        const typeChar = () => {
          if (!isMounted) return;
          if (charIndex < transcribed.length) {
            setDisplayText(transcribed.slice(0, charIndex + 1));
            charIndex++;
            timeout = setTimeout(typeChar, 35);
          } else {
            // Stop recording after typing complete
            timeout = setTimeout(() => {
              if (!isMounted) return;
              clearInterval(timerInterval);
              setPhase(2);
              setIsRecording(false);
              setTextColor("#f5f5f5");

              // Phase 3: Auto-fixed text appears (no button highlight - it's automatic)
              timeout = setTimeout(() => {
                if (!isMounted) return;
                setActiveAction(null);
                setDisplayText(fixed);
                setTextColor("#22c55e"); // Green to show it's auto-fixed

                // Phase 4: Change Tone - highlight button
                timeout = setTimeout(() => {
                  if (!isMounted) return;
                  setActiveAction("tone");
                  setTextColor("#a78bfa");

                  timeout = setTimeout(() => {
                    if (!isMounted) return;
                    setDisplayText(professional);
                    setTextColor("#8b5cf6");

                    // Phase 5: Translate - highlight button
                    timeout = setTimeout(() => {
                      if (!isMounted) return;
                      setActiveAction("translate");
                      setTextColor("#60a5fa");

                      timeout = setTimeout(() => {
                        if (!isMounted) return;
                        setDisplayText(translated);
                        setTextColor("#6366f1");

                        // Phase 6: Done - show copy success
                        timeout = setTimeout(() => {
                          if (!isMounted) return;
                          setActiveAction("copy");
                          setPhase(6);

                          // Restart loop
                          timeout = setTimeout(runDemo, 3000);
                        }, 1500);
                      }, 700);
                    }, 1200);
                  }, 700);
                }, 1200);
              }, 700);
            }, 500);
          }
        };

        timeout = setTimeout(typeChar, 300);
      }, 800);
    };

    runDemo();

    return () => {
      isMounted = false;
      clearTimeout(timeout);
      clearInterval(timerInterval);
    };
  }, []);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <div className="relative w-full max-w-[320px]">
      {/* Phone Frame - Larger */}
      <div 
        className="relative rounded-[2.5rem] p-2 shadow-2xl"
        style={{ 
          background: 'linear-gradient(145deg, #2d3748 0%, #1a202c 100%)',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.6)'
        }}
      >
        {/* Screen */}
        <div 
          className="rounded-[2rem] overflow-hidden"
          style={{ backgroundColor: '#0f1419' }}
        >
          {/* Header */}
          <div 
            className="h-11 flex items-center justify-between px-3"
            style={{ backgroundColor: '#1a2332', borderBottom: '1px solid #2d3748' }}
          >
            <Menu className="w-5 h-5" style={{ color: '#a0aec0' }} />
            
            {/* Recording indicator */}
            <div className="flex items-center gap-1.5">
              {isRecording && (
                <>
                  <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: '#ef4444' }} />
                  <span className="text-xs font-mono" style={{ color: '#ef4444' }}>{formatTime(recordingTime)}</span>
                </>
              )}
            </div>
            
            {/* Read Aloud */}
            <div 
              className="flex items-center gap-1 px-2 py-1 rounded-full text-[9px]"
              style={{ backgroundColor: '#10b981', color: '#fff' }}
            >
              <Volume2 className="w-3 h-3" />
              <span>Read Aloud</span>
            </div>
          </div>

          {/* Logo + Title */}
          <div className="flex flex-col items-center gap-1 py-3">
            <div className="w-10 h-10 flex items-center justify-center">
              <svg viewBox="0 0 64 64" className="w-9 h-9">
                <defs>
                  <linearGradient id="heroLogoGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" style={{ stopColor: '#6366f1' }} />
                    <stop offset="100%" style={{ stopColor: '#8b5cf6' }} />
                  </linearGradient>
                </defs>
                <circle cx="32" cy="32" r="28" fill="url(#heroLogoGrad)" opacity="0.15" />
                <path d="M32 16c-4.4 0-8 3.6-8 8v8c0 4.4 3.6 8 8 8s8-3.6 8-8v-8c0-4.4-3.6-8-8-8z" fill="url(#heroLogoGrad)" />
                <path d="M44 28v4c0 6.6-5.4 12-12 12s-12-5.4-12-12v-4h-2v4c0 7.2 5.4 13.1 12.4 13.9V50H26v2h12v-2h-4.4v-4.1c7-0.8 12.4-6.7 12.4-13.9v-4h-2z" fill="url(#heroLogoGrad)" />
              </svg>
            </div>
            <span className="text-base font-bold" style={{ color: '#f5f5f5' }}>Voice Capture</span>
          </div>

          {/* Voice Visualizer */}
          <div className="flex items-center justify-center gap-1 px-4 py-1">
            {[...Array(10)].map((_, i) => (
              <div
                key={i}
                className={`w-1 rounded-full transition-all duration-150 ${isRecording ? 'animate-pulse' : ''}`}
                style={{
                  height: isRecording ? `${8 + Math.random() * 14}px` : '4px',
                  backgroundColor: isRecording ? '#22d3ee' : '#2d3748',
                  animationDelay: `${i * 50}ms`
                }}
              />
            ))}
            <div className="ml-2 flex items-center gap-1 px-2 py-1 rounded-full text-[9px]" style={{ backgroundColor: '#1a2332', border: '1px solid #2d3748' }}>
              <span style={{ color: '#a0aec0' }}>Detected:</span>
              <span className="font-bold" style={{ color: '#10b981' }}>English</span>
            </div>
          </div>

          {/* Text Area */}
          <div className="mx-3 my-2 rounded-lg p-3 min-h-[70px]" style={{ backgroundColor: '#1a2332', border: '1px solid #2d3748' }}>
            <p 
              className="text-xs leading-relaxed transition-colors duration-300"
              style={{ color: textColor }}
            >
              {displayText || (
                <span style={{ color: '#4a5568' }}>
                  Press the microphone to start recording...
                </span>
              )}
              {phase <= 1 && displayText && <span className="animate-pulse text-purple-400">|</span>}
            </p>
          </div>

          {/* Action Buttons - Row 1 (5 buttons) */}
          <div className="flex items-center justify-center gap-1 px-3 mb-1">
            <ActionBtn icon={<Copy className="w-2.5 h-2.5" />} label="Copy" color={activeAction === "copy" ? "#22c55e" : "#4b5563"} isActive={activeAction === "copy"} />
            <ActionBtn icon="📜" label="History" color="#4b5563" />
            <ActionBtn icon="⚡" label="Shortcuts" color="#3b82f6" />
            <ActionBtn icon={<Download className="w-2.5 h-2.5" />} label="Download" color="#4b5563" />
            <ActionBtn label="Clear" color="#4b5563" />
          </div>

          {/* Action Buttons - Row 2 (4 buttons) */}
          <div className="flex items-center justify-center gap-1 px-3 mb-2">
            <ActionBtn 
              icon={<Sparkles className="w-2.5 h-2.5" />} 
              label="Tone" 
              color="#8b5cf6"
              isActive={activeAction === "tone"}
            />
            <ActionBtn 
              icon={<Languages className="w-2.5 h-2.5" />} 
              label="Translate" 
              color="#6366f1"
              isActive={activeAction === "translate"}
            />
            <div 
              className="flex items-center gap-0.5 px-2 py-1 rounded-full text-[9px]"
              style={{ backgroundColor: '#4b5563', color: '#fff' }}
            >
              <span>🇪🇸</span>
              <span>Spanish</span>
            </div>
            <ActionBtn label="GO" color="#10b981" bold />
          </div>

          {/* Record Button */}
          <div className="flex flex-col items-center gap-1 pb-4">
            <div
              className={`w-16 h-16 rounded-full flex items-center justify-center transition-all duration-300 ${
                isRecording ? 'scale-110' : 'scale-100'
              }`}
              style={{
                background: isRecording 
                  ? 'linear-gradient(135deg, #ef4444, #dc2626)' 
                  : 'linear-gradient(135deg, #3b82f6, #2563eb)',
                boxShadow: isRecording 
                  ? '0 0 30px rgba(239, 68, 68, 0.6)' 
                  : '0 0 20px rgba(59, 130, 246, 0.4)'
              }}
            >
              {isRecording ? (
                <Square className="w-6 h-6 text-white" fill="white" />
              ) : (
                <Mic className="w-7 h-7 text-white" />
              )}
            </div>
            <p className="text-[10px]" style={{ color: '#a0aec0' }}>
              {isRecording ? "Recording... Click to stop" : "Click to record"}
            </p>
          </div>

          {/* Step Indicator */}
          <div className="flex justify-center gap-2 pb-2">
            {['Record', 'Auto-Fix', 'Tone', 'Translate'].map((step, idx) => {
              const isActive = idx === 0 ? (phase >= 1 && phase <= 2) : 
                              idx === 1 ? (phase >= 2 && activeAction === null && displayText.includes("Hello")) :
                              idx === 2 ? activeAction === 'tone' :
                              activeAction === 'translate';
              return (
                <div 
                  key={step}
                  className="flex items-center gap-1 px-2 py-1 rounded-full text-[8px]"
                  style={{
                    backgroundColor: isActive ? '#6366f120' : 'transparent',
                    color: isActive ? '#a78bfa' : '#4a5568',
                    border: isActive ? '1px solid #6366f140' : '1px solid #2d374850'
                  }}
                >
                  {step}
                </div>
              );
            })}
          </div>

          {/* Home Indicator */}
          <div className="flex justify-center pb-2">
            <div className="w-24 h-1 rounded-full" style={{ backgroundColor: '#2d3748' }} />
          </div>
        </div>
      </div>
      
      {/* Glow effect */}
      <div 
        className="absolute -inset-4 rounded-[3rem] -z-10 opacity-20 blur-xl"
        style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6, #22d3ee)' }}
      />
    </div>
  );
}

// Compact Action Button
function ActionBtn({ icon, label, color, isActive, textDark, bold }) {
  return (
    <div
      className={`flex items-center gap-0.5 rounded-full transition-all duration-300 ${
        isActive ? 'scale-105 ring-1 ring-white/30' : ''
      }`}
      style={{
        backgroundColor: color,
        color: textDark ? '#000' : '#fff',
        padding: '4px 8px',
        fontSize: '9px',
        fontWeight: bold ? 'bold' : 'normal',
        boxShadow: isActive ? `0 0 12px ${color}80` : 'none'
      }}
    >
      {icon && <span className="flex items-center">{icon}</span>}
      <span>{label}</span>
    </div>
  );
}
